import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * HourStrip — a 24-cell horizontal intensity strip for hour-of-day activity.
 *
 * Sequential encoding: one hue, light -> dark (bmlc-navy-50 .. bmlc-navy-600),
 * with the peak hour called out in bmlc-gold-500. A ramp key is always present,
 * because a value carried by color alone needs a scale legend.
 */

export interface HourStripDatum {
  hour: number;
  value: number;
}

export interface HourStripProps {
  data: HourStripDatum[];
}

/* --- BMLC sequential ramp (single hue, light -> dark) --------------------- */
const RAMP = [
  '#f0f4f8', // navy-50  — empty
  '#e0e9f2', // navy-100
  '#c5d4e5', // navy-200
  '#a3b8d4', // navy-300
  '#7a9ac0', // navy-400
  '#1e3a5f', // navy-500
  '#1a3252', // navy-600
];
const PEAK = '#d4a017'; // gold-500 — the accent reserved for the busiest hour
const CELL_EDGE = 'rgba(30, 58, 95, 0.08)';
const INK = '#6b7280'; // gray-500
const LABEL_HOURS = [0, 6, 12, 18, 23];

const pad2 = (n: number): string => (n < 10 ? '0' + n : String(n));

const fmt = (n: number): string =>
  String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

function useContainerWidth(fallback: number): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const next = el.clientWidth;
      if (next > 0) setWidth(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const HourStrip: React.FC<HourStripProps> = ({ data }) => {
  const [wrapRef, measured] = useContainerWidth(640);
  const svgRef = useRef<SVGSVGElement>(null);
  const [active, setActive] = useState<number | null>(null);

  // Always 24 buckets, whatever the caller sends.
  const hours = useMemo(() => {
    const buckets: number[] = [];
    for (let i = 0; i < 24; i++) buckets.push(0);
    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      const hr = Math.floor(Number(d.hour));
      const v = Number(d.value);
      if (hr >= 0 && hr <= 23 && Number.isFinite(v) && v > 0) buckets[hr] += v;
    }
    return buckets;
  }, [data]);

  const { max, total, peakHour } = useMemo(() => {
    let mx = 0;
    let tot = 0;
    let ph = -1;
    for (let i = 0; i < 24; i++) {
      tot += hours[i];
      if (hours[i] > mx) {
        mx = hours[i];
        ph = i;
      }
    }
    return { max: mx, total: tot, peakHour: ph };
  }, [hours]);

  const w = Math.max(200, measured);
  const gap = 2; // the surface gap that separates neighbouring cells
  const cellH = w < 360 ? 26 : 34;
  const labelBand = 16;
  const h = cellH + labelBand;
  const cellW = Math.max(2, (w - gap * 23) / 24);
  const pitch = cellW + gap;
  const cellFont = w < 360 ? 8 : 9;

  const cellX = (i: number): number => i * pitch;

  const fillFor = (i: number): string => {
    const v = hours[i];
    if (v <= 0 || max <= 0) return RAMP[0];
    if (i === peakHour) return PEAK;
    const t = v / max;
    const levels = RAMP.length - 1; // 6 non-empty steps
    const lvl = Math.min(levels - 1, Math.max(0, Math.ceil(t * levels) - 1));
    return RAMP[lvl + 1];
  };

  const isEmpty = data.length === 0 || total === 0;

  const summary = useMemo(() => {
    if (isEmpty) return 'Activity by hour of day — no data for this period';
    return `Activity by hour of day. ${fmt(total)} questions in total; busiest hour ${pad2(
      peakHour
    )}:00 with ${fmt(max)}.`;
  }, [isEmpty, total, peakHour, max]);

  const tipX =
    active !== null ? Math.max(58, Math.min(w - 58, cellX(active) + cellW / 2)) : 0;

  return (
    <div ref={wrapRef} className="relative w-full">
      {/* Readout band: the ramp key when idle, the tooltip on hover — one row,
          so nothing is clipped and the layout never jumps. */}
      <div className="relative mb-1.5 h-7">
        {active !== null ? (
          <div
            className="pointer-events-none absolute top-0 whitespace-nowrap rounded-lg border border-bmlc-navy-100 bg-white/95 px-2.5 py-1 shadow-soft-sm"
            style={{ left: tipX, transform: 'translateX(-50%)' }}
          >
            <span className="font-serif text-xs font-bold text-bmlc-navy-900">
              {pad2(active)}:00
            </span>
            <span className="mx-1 text-[11px] text-gray-400">—</span>
            <span className="text-[11px] text-gray-600">
              {fmt(hours[active])} {hours[active] === 1 ? 'question' : 'questions'}
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2 pt-1">
            {isEmpty ? (
              <span className="text-[11px] text-gray-400">No data for this period</span>
            ) : (
              <>
                <span className="text-[10px] uppercase tracking-wider text-gray-400">Less</span>
                <span className="flex items-center gap-0.5">
                  {RAMP.map((c) => (
                    <span
                      key={c}
                      className="inline-block h-2.5 w-2.5 rounded-[2px]"
                      style={{ backgroundColor: c, boxShadow: `inset 0 0 0 1px ${CELL_EDGE}` }}
                    />
                  ))}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-gray-400">More</span>
                <span className="ml-1.5 flex items-center gap-1">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-[2px]"
                    style={{ backgroundColor: PEAK }}
                  />
                  <span className="text-[10px] uppercase tracking-wider text-gray-400">Peak</span>
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <svg
        ref={svgRef}
        role="img"
        aria-label={summary}
        viewBox={`0 0 ${w} ${h}`}
        style={{ display: 'block', width: '100%', height: h }}
        onPointerLeave={() => setActive(null)}
      >
        {hours.map((v, i) => (
          <rect
            key={`cell-${i}`}
            x={cellX(i)}
            y={0}
            width={cellW}
            height={cellH}
            rx={3}
            fill={fillFor(i)}
            stroke={CELL_EDGE}
            strokeWidth={active === i ? 0 : 1}
            pointerEvents="none"
          />
        ))}

        {/* Hover ring on the active cell — surface-coloured, sits on top */}
        {active !== null && (
          <rect
            x={cellX(active) - 1}
            y={-1}
            width={cellW + 2}
            height={cellH + 2}
            rx={4}
            fill="none"
            stroke="#1e3a5f"
            strokeWidth={1.5}
            pointerEvents="none"
          />
        )}

        {/* Hour labels at 0 / 6 / 12 / 18 / 23 */}
        {LABEL_HOURS.map((hr) => (
          <text
            key={`hl-${hr}`}
            x={hr === 0 ? 0 : hr === 23 ? cellX(23) + cellW : cellX(hr) + cellW / 2}
            y={cellH + 12}
            textAnchor={hr === 0 ? 'start' : hr === 23 ? 'end' : 'middle'}
            fontSize={cellFont}
            fill={INK}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {pad2(hr)}
          </text>
        ))}

        {/* Hit targets — full pitch, full height */}
        {hours.map((v, i) => (
          <rect
            key={`hit-${i}`}
            x={cellX(i) - gap / 2}
            y={0}
            width={pitch}
            height={h}
            fill="transparent"
            onPointerEnter={() => setActive(i)}
            onPointerDown={() => setActive(i)}
          />
        ))}
      </svg>

      {/* Table twin — the values are never gated behind a hover */}
      <table className="sr-only">
        <caption>{summary}</caption>
        <thead>
          <tr>
            <th scope="col">Hour</th>
            <th scope="col">Questions</th>
          </tr>
        </thead>
        <tbody>
          {hours.map((v, i) => (
            <tr key={`row-${i}`}>
              <th scope="row">{`${pad2(i)}:00`}</th>
              <td>{fmt(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default HourStrip;
